import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import ParsedTable from './ParsedTable';
import { JsonViewer } from '@textea/json-viewer';
import ReactMarkdown from 'react-markdown';

// Helper: Extract tables from text and split into parts
function splitTextByTables(text) {
  if (typeof text !== 'string') return [text];
  // Regex for Markdown tables: lines with pipes and at least one divider row
  const tableRegex = /((?:^\s*\|.*\|\s*$\n?)+)/gm;
  const parts = [];
  let lastIndex = 0;
  let match;
  while ((match = tableRegex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push({ type: 'text', value: text.slice(lastIndex, match.index) });
    }
    parts.push({ type: 'table', value: match[0] });
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < text.length) {
    parts.push({ type: 'text', value: text.slice(lastIndex) });
  }
  return parts;
}

// Recursively parse 'text' fields that are JSON strings
function parseJsonFields(obj) {
  if (Array.isArray(obj)) {
    return obj.map(parseJsonFields);
  } else if (obj && typeof obj === 'object') {
    const newObj = { ...obj };
    for (const key of Object.keys(newObj)) {
      if (key === 'text' && typeof newObj[key] === 'string') {
        try {
          const parsed = JSON.parse(newObj[key]);
          newObj[key] = parseJsonFields(parsed);
        } catch (e) {
          // leave as string if not valid JSON
        }
      } else {
        newObj[key] = parseJsonFields(newObj[key]);
      }
    }
    return newObj;
  }
  return obj;
}

function tryFormatJson(jsonString) {
  try {
    const obj = typeof jsonString === 'string' ? JSON.parse(jsonString) : jsonString;
    return { parsed: parseJsonFields(obj), formatted: JSON.stringify(obj, null, 2), error: null };
  } catch (e) {
    return { parsed: null, formatted: null, error: e };
  }
}

const VALID_IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp', 'image/gif'];

// Helper: Detect if a string contains markdown syntax
function isMarkdown(text) {
  if (typeof text !== 'string') return false;
  // Heuristic: look for markdown features
  return /(^#|\*\*|__|\* |\d+\. |\[.*\]\(.*\)|`|^>|^- |\n\s*\n)/m.test(text);
}

function MessageItem({ message }) {
  const { sender, createdAt, isCreatedByUser, text, content, files, tokenCount } = message;
  const [reasoningOpen, setReasoningOpen] = useState(false);
  const [toolCallOpen, setToolCallOpen] = useState({}); // key: idx, value: open/closed
  const [toolArgsOpen, setToolArgsOpen] = useState({});
  const [modalImage, setModalImage] = useState(null);

  // Close modal on Escape key
  useEffect(() => {
    if (!modalImage) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        setModalImage(null);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [modalImage]);

  const formatDate = (dateString) => {
    try {
      return format(new Date(dateString), 'PPpp');
    } catch (e) {
      return 'Invalid date';
    }
  };

  const senderDisplayName = sender === 'User' ? 'You' : sender;

  // Filter valid image files
  const validImages = Array.isArray(files)
    ? files.filter(f => f && VALID_IMAGE_TYPES.includes(f.type) && f.filepath)
    : [];

  return (
    <div className={`message-item ${isCreatedByUser ? 'user' : 'agent'}`}>
      <div className="message-sender" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span>{senderDisplayName}</span>
        {typeof tokenCount === 'number' && (
          <span style={{ fontSize: '0.8em', color: '#888', marginLeft: 4, background: '#f4f5f7', borderRadius: 4, padding: '1px 7px' }} title="Token count">
            {tokenCount} tokens
          </span>
        )}
      </div>
      {/* If sender is User, always show the text field */}
      {sender === 'User' && text && (
        isMarkdown(text) ? (
          <ReactMarkdown 
            components={{
              img: ({node, ...props}) => (
                <span
                  style={{ display: 'inline-block', cursor: 'pointer' }}
                  onClick={e => { e.stopPropagation(); setModalImage(props.src); }}
                  tabIndex={0}
                  role="button"
                  aria-label="View image larger"
                >
                  <img {...props} alt={props.alt || ''} style={{maxWidth: '100%', height: 'auto', display: 'block', margin: '8px 0'}} className="markdown-img" />
                </span>
              )
            }}
          >{text}</ReactMarkdown>
        ) : (
          <span>{text}</span>
        )
      )}
      {/* If sender is User and there are valid images, show them */}
      {sender === 'User' && validImages.length > 0 && (
        <div className="user-images-scroll">
          {validImages.map((img, idx) => (
            <img
              key={img.filepath || idx}
              src={img.filepath}
              alt={`attachment-${idx}`}
              className="user-image-thumb"
              onClick={e => { e.stopPropagation(); setModalImage(img.filepath); }}
              style={{ cursor: 'pointer' }}
            />
          ))}
        </div>
      )}
      {/* Modal for large image view */}
      {modalImage && (
        <div className="image-modal-overlay" onClick={() => setModalImage(null)}>
          <div className="image-modal-content" onClick={e => e.stopPropagation()}>
            <button className="image-modal-close" onClick={() => setModalImage(null)}>&times;</button>
            <img src={modalImage} alt="fullsize" className="image-modal-img" />
          </div>
        </div>
      )}
      {/* For non-user senders, use the content array logic as before */}
      {sender !== 'User' && Array.isArray(content) && content.length > 0 && content.map((item, idx) => {
        if (!item || typeof item !== 'object') return null;
        // THINK SECTION (collapsible)
        if (item.type === 'think') {
          return (
            <div key={`think-${idx}`} className="message-section think-section">
              <div className="section-label" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span>Reasoning</span>
                <button
                  className="reasoning-toggle-btn"
                  onClick={() => setReasoningOpen((open) => !open)}
                  aria-label={reasoningOpen ? 'Collapse Reasoning' : 'Expand Reasoning'}
                  style={{ marginLeft: 8, fontSize: '0.9em', padding: '2px 8px', borderRadius: 4, border: '1px solid #b5d6f6', background: '#e6f7ff', cursor: 'pointer' }}
                >
                  {reasoningOpen ? 'Hide' : 'Show'}
                </button>
              </div>
              {reasoningOpen && <div>{item.think}</div>}
            </div>
          );
        }
        // TEXT SECTION (now called Response)
        if (item.type === 'text') {
          if (!item.text || item.text.trim() === '') return null;
          const parts = splitTextByTables(item.text);
          return (
            <div key={`text-${idx}`} className="message-section text-section">
              <div className="section-label">Response</div>
              {parts.map((part, i) =>
                part.type === 'table' ? (
                  <ParsedTable key={`table-${i}`} rawText={part.value} />
                ) : isMarkdown(part.value) ? (
                  <ReactMarkdown 
                    key={`md-${i}`}
                    components={{
                      img: ({node, ...props}) => (
                        <span
                          style={{ display: 'inline-block', cursor: 'pointer' }}
                          onClick={e => { e.stopPropagation(); setModalImage(props.src); }}
                          tabIndex={0}
                          role="button"
                          aria-label="View image larger"
                        >
                          <img {...props} alt={props.alt || ''} style={{maxWidth: '100%', height: 'auto', display: 'block', margin: '8px 0'}} className="markdown-img" />
                        </span>
                      )
                    }}
                  >{part.value}</ReactMarkdown>
                ) : (
                  <span key={`textpart-${i}`}>{part.value}</span>
                )
              )}
            </div>
          );
        }
        // TOOL CALL SECTION
        if (item.type === 'tool_call' && item.tool_call) {
          const outputString = typeof item.tool_call.output === 'string' ? item.tool_call.output : JSON.stringify(item.tool_call.output);
          const { parsed, error } = tryFormatJson(outputString);
          const isOutputOpen = !!toolCallOpen[idx];

          const argsString = typeof item.tool_call.args === 'string' ? item.tool_call.args : JSON.stringify(item.tool_call.args);
          const { parsed: parsedArgs, error: argsError } = tryFormatJson(argsString);
          const areArgsOpen = !!toolArgsOpen[idx];

          return (
            <div key={`toolcall-${idx}`} className="message-section toolcall-section">
              <div className="section-label">Tool Call</div>
              <div className="toolcall-name">{item.tool_call.name}</div>
              
              {/* Args Section */}
              <div className="toolcall-args-label" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '4px' }}>
                <span>Input</span>
                <button
                  className="toolcall-toggle-btn"
                  onClick={() => setToolArgsOpen((prev) => ({ ...prev, [idx]: !areArgsOpen }))}
                  aria-label={areArgsOpen ? 'Collapse Input' : 'Expand Input'}
                  style={{ marginLeft: 8, fontSize: '0.9em', padding: '2px 8px', borderRadius: 4, border: '1px solid #fae3b0', background: '#fffbe6', cursor: 'pointer' }}
                >
                  {areArgsOpen ? 'Hide' : 'Show'}
                </button>
              </div>
              {areArgsOpen && (
                <div className="toolcall-args toolcall-json-output" style={{ marginTop: '4px' }}>
                  {argsError ? (
                    <pre>{argsString}</pre>
                  ) : (
                    <JsonViewer
                      value={parsedArgs}
                      rootName={false}
                      defaultInspectDepth={1}
                      displayDataTypes={false}
                      displayObjectSize={false}
                    />
                  )}
                </div>
              )}

              {/* Output Section */}
              <div className="toolcall-output-label" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '8px' }}>
                <span>Output</span>
                <button
                  className="toolcall-toggle-btn"
                  onClick={() => setToolCallOpen((prev) => ({ ...prev, [idx]: !isOutputOpen }))}
                  aria-label={isOutputOpen ? 'Collapse Output' : 'Expand Output'}
                  style={{ marginLeft: 8, fontSize: '0.9em', padding: '2px 8px', borderRadius: 4, border: '1px solid #fae3b0', background: '#fffbe6', cursor: 'pointer' }}
                >
                  {isOutputOpen ? 'Hide' : 'Show'}
                </button>
              </div>
              {isOutputOpen && (
                <div className="toolcall-json-output" style={{ marginTop: '4px' }}>
                  {error ? (
                    <pre>{outputString}</pre>
                  ) : (
                    <JsonViewer
                      value={parsed}
                      rootName={false}
                      defaultInspectDepth={1}
                      displayDataTypes={false}
                      displayObjectSize={false}
                    />
                  )}
                </div>
              )}
            </div>
          );
        }
        // Unknown type fallback
        return (
          <div key={`unknown-${idx}`} className="message-section unknown-section">
            <div className="section-label">Unknown Content Type</div>
            <pre>{JSON.stringify(item, null, 2)}</pre>
          </div>
        );
      })}
      <div className="message-timestamp">{formatDate(createdAt)}</div>
    </div>
  );
}

export default MessageItem; 